import { useState } from "react";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Reveal, Hairline, useSweep } from "@/components/primitives";
import { submitInquiry, uploadAssessmentFile, formatApiError } from "@/lib/api";

const REVIEW_TYPES = [
  "Full Organizational Review: People, Operations, Profitability",
  "Leadership and Workforce Focus",
  "Growth and Financial Focus",
  "Culture and Strategy Review",
  "Tailored Assessment",
];

const Beat = ({ n, title, children }) => (
  <Reveal>
    <div className="mb-12">
      <div className="flex items-center gap-4 mb-6">
        <span className="font-display gold-text text-lg">{n}</span>
        <span className="h-px flex-1 bg-gradient-to-r from-[var(--gold)] to-transparent" />
        <span className="font-display uppercase tracking-luxe text-[10px] md:text-xs text-[var(--bone)]/80">{title}</span>
      </div>
      {children}
    </div>
  </Reveal>
);

export default function StewardAssessment() {
  const [email, setEmail] = useState("");
  const [reviewType, setReviewType] = useState("");
  const [context, setContext] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const sweep = useSweep();

  const inputCls =
    "w-full bg-transparent border-b border-[rgba(180,139,63,0.35)] px-1 py-3 text-[var(--bone)] font-serif2 text-lg placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Please enter a valid email.");
    if (!reviewType) return toast.error("Please select the type of review you are requesting.");
    setLoading(true);
    try {
      // Upload supporting documents first; only proceed if every upload succeeds.
      const fileIds = [];
      for (const f of files) {
        const res = await uploadAssessmentFile(f);
        fileIds.push(res.file_id);
      }
      await submitInquiry({
        type: "assessment",
        email,
        review_type: reviewType,
        division: "Steward & Stone — Executive Assessment",
        message: context,
        attachments: fileIds,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Submission failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glow-navy min-h-screen">
      <Seo title="Steward & Stone — Executive Assessment Inquiry" description="Request a Steward & Stone executive assessment. Every engagement begins with understanding the question." />

      <section className="pt-28 md:pt-36 pb-8 px-6 text-center">
        <Reveal>
          <h1 className="sr-only">Steward &amp; Stone Executive Assessment Inquiry</h1>
          <img src="/brand/steward-stone-web.png" alt="Steward & Stone — Executive Advisory" className="mx-auto w-full max-w-xl" />
          <p className="mt-4 font-display uppercase tracking-luxe text-[10px] md:text-xs text-[var(--gold)]/80">Executive Assessment Inquiry</p>
          <p className="mt-8 max-w-2xl mx-auto font-serif2 italic text-xl md:text-3xl text-[var(--bone)]/90">
            Every engagement begins with understanding the question.
          </p>
          <p className="mt-5 max-w-xl mx-auto text-[var(--bone)]/70 leading-relaxed">
            The details below help Steward &amp; Stone understand the review you are requesting and the context surrounding it. Submitting an inquiry begins a conversation — it is not an acceptance of engagement.
          </p>
        </Reveal>
      </section>

      <section className="pb-24 px-6">
        <div className="max-w-2xl mx-auto">
          <Hairline className="opacity-60 mb-14" />

          {done ? (
            <div className="corner-frame px-8 py-16 text-center" data-testid="assessment-success">
              <p className="font-display gold-text uppercase tracking-wide2 text-xl">Inquiry Received</p>
              <p className="mt-5 text-[var(--bone)]/80 leading-relaxed">
                Thank you. Your executive assessment inquiry has been received and will be reviewed confidentially by Steward &amp; Stone. If it aligns with our current focus, we will reach out to continue the conversation.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} data-testid="assessment-form">
              <Beat n="I" title="About You">
                <input type="email" className={inputCls} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="assessment-email" />
              </Beat>

              <Beat n="II" title="What would you like us to review?">
                <div className="grid grid-cols-1 gap-3" role="radiogroup" aria-label="Type of review">
                  {REVIEW_TYPES.map((r) => (
                    <button
                      type="button"
                      key={r}
                      onClick={() => setReviewType(r)}
                      data-testid={`assessment-review-${REVIEW_TYPES.indexOf(r)}`}
                      className={`text-left px-5 py-4 border transition-all duration-300 font-serif2 text-base md:text-lg ${reviewType === r ? "border-[var(--gold)] bg-[rgba(180,139,63,0.07)] text-[var(--gold-lite)]" : "border-[rgba(180,139,63,0.22)] text-[var(--bone)]/85 hover:border-[var(--gold)]/60"}`}
                    >
                      <span className="inline-block w-2 h-2 mr-3 rounded-full align-middle" style={{ background: reviewType === r ? "var(--gold)" : "transparent", border: "1px solid var(--gold)" }} />
                      {r}
                    </button>
                  ))}
                </div>
              </Beat>

              <Beat n="III" title="Tell us what matters">
                <textarea
                  className={`${inputCls} border border-[rgba(180,139,63,0.35)] px-4`}
                  rows={5}
                  placeholder="In your own words, tell us what would help us understand the situation — the question you are facing, and the context surrounding it."
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  data-testid="assessment-context"
                />
              </Beat>

              <Beat n="IV" title="Supporting materials (optional)">
                <label className="block border border-dashed border-[rgba(180,139,63,0.35)] px-5 py-6 text-center cursor-pointer hover:border-[var(--gold)] transition-colors" data-testid="assessment-files-label">
                  <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => {
                    const allowed = ["pdf", "doc", "docx", "xls", "xlsx", "csv", "txt", "png", "jpg", "jpeg", "webp"];
                    const valid = [];
                    for (const f of Array.from(e.target.files || [])) {
                      const ext = (f.name.split(".").pop() || "").toLowerCase();
                      if (!allowed.includes(ext)) { toast.error(`${f.name}: unsupported file type.`); continue; }
                      if (f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: exceeds the 15 MB limit.`); continue; }
                      valid.push(f);
                    }
                    setFiles(valid.slice(0, 8));
                  }} data-testid="assessment-files" />
                  <span className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold-lite)]">
                    {files.length ? `${files.length} file(s) selected` : "Attach reports or documents"}
                  </span>
                  {files.length > 0 && (
                    <p className="mt-2 text-xs text-[var(--muted)]">{files.map((f) => f.name).join(", ")}</p>
                  )}
                </label>
              </Beat>

              <div className="mt-14 text-center">
                <button
                  disabled={loading}
                  {...sweep}
                  className="jmh-cta disabled:opacity-50"
                  data-testid="assessment-submit"
                >
                  {loading ? "Submitting…" : "Submit Assessment Inquiry"}
                </button>
                <p className="mt-6 text-[11px] text-[var(--muted)]">All inquiries are reviewed confidentially. A response will be provided at our discretion.</p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
